import React, { useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  Paper,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Alert,
  Snackbar,
  Tabs,
  Tab,
} from '@mui/material';
import { registerVolunteer, requestLoginCode, verifyLoginCode, storeAuthData } from '../services/api';
import { Volunteer } from '../models/types';
import { useAuth } from '../contexts/AuthContext';

const commodityOptions = [
  'Musikverein',
  'Kameradschaftsbund'
];

interface VolunteerRegistrationFormProps {
  onSuccess?: (volunteer: Volunteer) => void;
}

interface FormErrors {
  [key: string]: string;
}

const VolunteerRegistrationForm: React.FC<VolunteerRegistrationFormProps> = ({ onSuccess }) => {
  const { setAuthState } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phoneNumber: '',
    commodity: '',
    otherCommodity: '',
    code: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // UI state
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [step, setStep] = useState<'email' | 'code'>('email');

  const validate = () => {
    const newErrors: FormErrors = {};

    if (activeTab === 'login') {
      if (step === 'email') {
        if (!formData.email) newErrors.email = 'E-Mail ist erforderlich';
      } else {
        if (!formData.code) newErrors.code = 'Code ist erforderlich';
      }
    } else {
      if (!formData.firstname) newErrors.firstname = 'Vorname ist erforderlich';
      if (!formData.lastname) newErrors.lastname = 'Nachname ist erforderlich';
      if (!formData.email) newErrors.email = 'E-Mail ist erforderlich';
      if (formData.commodity === 'Sonstige' && !formData.otherCommodity) {
        newErrors.otherCommodity = 'Bitte geben Sie Ihre Organisation an';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleRequestNewCode = async () => {
    try {
      await requestLoginCode({ email: formData.email });
      setSuccessMessage('Neuer Code wurde per E-Mail gesendet');
      setShowSnackbar(true);
    } catch (error) {
      console.error('Failed to request new code:', error);
      setErrors({ submit: 'Fehler beim Senden des Codes. Bitte versuchen Sie es erneut.' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);

    try {
      if (activeTab === 'login') {
        if (step === 'email') {
          await requestLoginCode({ email: formData.email });
          setStep('code');
          setSuccessMessage('Code wurde per E-Mail gesendet');
          setShowSnackbar(true);
        } else {
          const response = await verifyLoginCode({
            email: formData.email,
            code: formData.code
          });          const authState = storeAuthData(response);
          setAuthState({
            isAuth: authState.isAuthenticated,
            isAdmin: authState.isAdmin,
            token: authState.sessionToken,
            volunteer: authState.volunteer,
            callback: () => onSuccess && onSuccess(response.volunteer)
          });

          setSuccessMessage('Anmeldung erfolgreich!');
          setShowSnackbar(true);
        }      } else {
        try {
          const response = await registerVolunteer({
            name: `${formData.firstname.trim()} ${formData.lastname.trim()}`.trim(),
            email: formData.email,
            phoneNumber: formData.phoneNumber || undefined,
            commodity: formData.commodity === 'Sonstige'
              ? formData.otherCommodity
              : formData.commodity,          });
          if (response.volunteer && response.volunteer.accessToken) {            const authData = {
              sessionToken: response.volunteer.accessToken,
              isAdmin: false,
              expiresIn: 0,
              volunteer: response.volunteer
            };            storeAuthData(authData);
            setAuthState({
              isAuth: true,
              isAdmin: false,
              token: response.volunteer.accessToken,
              volunteer: response.volunteer,
              callback: () => onSuccess && onSuccess(response.volunteer)
            });

            setSuccessMessage('Registrierung erfolgreich!');
            setShowSnackbar(true);
          }

          // Reset form
          setFormData({
            firstname: '',
            lastname: '',
            email: '',
            phoneNumber: '',
            commodity: '',
            otherCommodity: '',
            code: ''
          });

        } catch (err: any) {
          if (err.response?.data?.error === 'Email already registered') {
            setFormData(prev => ({
              ...prev,
              firstname: '',
              lastname: '',
              phoneNumber: '',
              commodity: '',
              otherCommodity: '',
            }));
            setErrors({
              email: 'Diese E-Mail-Adresse ist bereits registriert.'
            });
            setActiveTab('login');
            setStep('email');
            return;
          }
          throw err;
        }
      }
    } catch (error: any) {
      console.error('Registration/Login failed:', error);
      let errorMessage = 'Vorgang fehlgeschlagen. Bitte versuchen Sie es später erneut.';
      if (activeTab === 'login' && error.response?.status === 401) {
        errorMessage = 'Ungültiger Code. Bitte versuchen Sie es erneut.';
      }
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleTabChange = (_: React.SyntheticEvent, newValue: 'login' | 'register') => {
    setActiveTab(newValue);
    if (newValue === 'login') {
      setStep('email');
    }
    setErrors({});
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        width: '100%',
        backgroundColor: 'background.paper',
        borderRadius: '0.5rem',
      }}
    >
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        centered
        sx={{
          mb: 4,
          '& .MuiTabs-indicator': {
            backgroundColor: 'primary.main',
          },
          '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 500,
            minWidth: '120px',
            '&.Mui-selected': {
              color: 'primary.main',
            },
          },
        }}
      >
        <Tab label="Login" value="login" />
        <Tab label="Neue Registrierung" value="register" />
      </Tabs>

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <TextField
          required
          label="E-Mail"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          error={!!errors.email}
          helperText={errors.email}
          variant="outlined"
          disabled={activeTab === 'login' && step === 'code'}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '0.375rem',
            },
          }}
        />

        {activeTab === 'login' && step === 'code' && (
          <>
            <TextField
              required
              label="Code"
              name="code"
              type="text"
              value={formData.code}
              onChange={handleInputChange}
              error={!!errors.code}
              helperText={errors.code}
              variant="outlined"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.375rem',
                },
              }}
            />
            <Button
              type="button"
              variant="text"
              color="primary"
              onClick={handleRequestNewCode}
              sx={{ alignSelf: 'flex-end', mt: -2 }}
            >
              Neuen Code anfordern
            </Button>
          </>
        )}

        {activeTab === 'register' && (
          <>
            <TextField
              required
              label="Vorname"
              name="firstname"
              value={formData.firstname}
              onChange={handleInputChange}
              error={!!errors.firstname}
              helperText={errors.firstname}
              variant="outlined"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.375rem',
                },
              }}
            />

            <TextField
              required
              label="Nachname"
              name="lastname"
              value={formData.lastname}
              onChange={handleInputChange}
              error={!!errors.lastname}
              helperText={errors.lastname}
              variant="outlined"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.375rem',
                },
              }}
            />

            <TextField
              label="Telefonnummer"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber}
              variant="outlined"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.375rem',
                },
              }}
            />

            <FormControl fullWidth>
              <InputLabel id="commodity-label">Organisation</InputLabel>
              <Select
                labelId="commodity-label"
                name="commodity"
                value={formData.commodity}
                onChange={handleSelectChange}
                label="Organisation"
                sx={{
                  borderRadius: '0.375rem',
                }}
              >
                <MenuItem value="">
                  <em>Keine</em>
                </MenuItem>
                {commodityOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {formData.commodity === 'Sonstige' && (
              <TextField
                label="Andere Organisation"
                name="otherCommodity"
                value={formData.otherCommodity}
                onChange={handleInputChange}
                error={!!errors.otherCommodity}
                helperText={errors.otherCommodity}
                variant="outlined"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.375rem',
                  },
                }}
              />
            )}
          </>
        )}

        {errors.submit && (
          <Box>
            <Typography color="error">{errors.submit}</Typography>
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            sx={{
              py: 1.5,
              fontWeight: 600,
              fontSize: '1rem',
            }}
          >
            {isSubmitting
              ? 'Verarbeite...'
              : activeTab === 'login'
                ? step === 'email'
                  ? 'Code anfordern'
                  : 'Anmelden'
                : 'Registrieren'}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity="success"
          sx={{
            width: '100%',
            borderRadius: '0.375rem',
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default VolunteerRegistrationForm;

